import { Cog8ToothIcon, HomeModernIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { UserInfo } from "@/api/rest/models/account";
import { Building } from "@/api/rest/models/building";
import { Household } from "@/api/rest/models/household";
import { naturallySortByTitle } from "@/utils/collection";
import { debug } from "@/utils/debug/logger";
import { noOrphanSuffix } from "@/utils/text";

const COLLAPSED_KEY = "dobu-sidebar-collapsed-buildings";

function BuildingHeaderTitle({ street }: { street: string }) {
  return (
    <>
      <HomeModernIcon aria-hidden="true" className="mt-0.5 h-6 w-6 shrink-0" />
      <span className="min-w-0 flex-1 text-balance leading-snug">{noOrphanSuffix(street)}</span>
    </>
  );
}

function loadCollapsed(): Set<number> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    if (raw) {
      return new Set(JSON.parse(raw));
    }
  } catch {
    return new Set();
  }
  return new Set();
}

function saveCollapsed(set: Set<number>) {
  localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...set]));
}

type NavbarMenuProps = {
  isOpen: boolean;
  userName: string;
  user: UserInfo | null;
  buildings: (Building & { households: Household[] })[];
  onNavigate: () => void;
};

// Updated NavbarMenu Component
export const NavbarMenu = memo(function NavbarMenu({ isOpen, userName, user, buildings, onNavigate }: NavbarMenuProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Set<number>>(loadCollapsed);

  const toggleCollapsed = useCallback((buildingId: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(buildingId)) {
        next.delete(buildingId);
      } else {
        next.add(buildingId);
      }
      saveCollapsed(next);
      return next;
    });
  }, []);

  const translations = useMemo(
    () => ({
      myResidences: t("home.myResidences"),
      logout: t("login.logout"),
    }),
    [t],
  ); // Only recreate when t function changes

  // Base Layout & Responsiveness
  const baseLayoutClasses = clsx(
    // Container basics
    "flex-col justify-stretch w-full",
    // Responsive display
    "md:flex",
    isOpen ? "flex" : "hidden",
    // Padding bottom for scrollbar
    // "pb-1",
  );

  // Scrollbar Configuration
  const scrollbarClasses = clsx(
    "md:overflow-y-scroll",
    "scrollbar-thin",
    "scrollbar-track-white",
    "scrollbar-thumb-slate-100",
    "md:flex-grow",
  );

  // Menu Item Base Styles (Account & Building Headers)
  const menuHeaderClasses = (isManager: boolean) =>
    clsx(
      "block w-full",
      // Spacing
      "px-4 py-2",
      // Typography
      "font-bold text-lg",
      // Interaction
      isManager ? "cursor-pointer" : "cursor-default",
      // Hover states
      isManager && "hover:bg-gray-100 hover:text-dobu-orange-focus",
    );

  // Nested Household Item Styles
  const householdItemClasses = clsx(
    "block",
    // Indentation & Spacing
    "pl-12 pr-8 py-1",
    // Typography
    "text-gray-700",
    // Interaction
    "cursor-pointer",
    // Hover states
    "hover:bg-gray-100 hover:text-dobu-orange-focus",
  );

  // Icon Styles
  const iconClasses = clsx(
    // Size
    "h-6 w-6",
    // Display & Alignment
    "inline-block",
    "mr-2 -mt-1",
  );

  // Spacer Utility
  const spacerClasses = clsx("hidden md:flex flex-grow");

  debug.log("NavbarMenu", "Props", {
    isOpen,
    userName,
    buildings,
  });

  const sepcialNames = ["Haldur"];

  const isManager = (buildingId: number) => {
    return user?.accounts.some((acc) => acc.managedBuildings.some((b) => b.buildingId === buildingId));
  };

  return (
    <div className={clsx(baseLayoutClasses, scrollbarClasses)}>
      {buildings.map((building) => {
        const collapsible = building.households.length > 1;
        const isCollapsed = collapsible && collapsed.has(building.buildingId);

        return (
          <Fragment key={building.buildingId}>
            {building.buildingId !== -1 && isManager(building.buildingId) ? (
              <div className="flex items-center">
                <NavLink
                  to={`/building/${building.buildingId}`}
                  onClick={onNavigate}
                  className={clsx(menuHeaderClasses(true), "flex min-w-0 flex-1 items-start gap-2")}
                >
                  <BuildingHeaderTitle street={building.street} />
                </NavLink>
                {collapsible && (
                  <span
                    className="p-2 mr-1 cursor-pointer text-gray-400 hover:text-gray-600"
                    onClick={() => toggleCollapsed(building.buildingId)}
                  >
                    <ChevronDownIcon
                      className={clsx("h-4 w-4 transition-transform duration-200", isCollapsed && "-rotate-90")}
                    />
                  </span>
                )}
              </div>
            ) : building.buildingId === -1 ? (
              <NavLink to="/household" onClick={onNavigate} className={menuHeaderClasses(true)}>
                <HomeModernIcon className={iconClasses} />
                {translations.myResidences}
              </NavLink>
            ) : collapsible ? (
              <span
                className={clsx(menuHeaderClasses(false), "!cursor-pointer flex items-start gap-2 select-none")}
                onClick={() => toggleCollapsed(building.buildingId)}
              >
                <BuildingHeaderTitle street={building.street} />
                <ChevronDownIcon
                  className={clsx(
                    "mt-1 h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200",
                    isCollapsed && "-rotate-90",
                  )}
                />
              </span>
            ) : (
              <span className={clsx(menuHeaderClasses(false), "flex items-start gap-2")}>
                <BuildingHeaderTitle street={building.street} />
              </span>
            )}
            {!isCollapsed && (
              <div>
                {naturallySortByTitle<Household>(building.households, sepcialNames).map((household: Household) => (
                  <NavLink
                    to={`/household/${household.id}`}
                    onClick={onNavigate}
                    key={household.id}
                    className={householdItemClasses}
                  >
                    {household.title}
                  </NavLink>
                ))}
              </div>
            )}
          </Fragment>
        );
      })}

      <span className={spacerClasses} />

      {userName && (
        <div className="sticky bottom-0 mt-1 w-full bg-white shadow-[0_-4px_6px_-2px_rgba(0,0,0,0.1)]">
          <NavLink to="/account" onClick={onNavigate} className={menuHeaderClasses(true)}>
            <Cog8ToothIcon className={iconClasses} />
            {userName}
          </NavLink>
        </div>
      )}
    </div>
  );
});
